import { Button, Icon, Input, InputWrapper, Modal } from "@components";
import { CreateNewInventoryType, InvetoryHistoryType, Product, Variations } from "@utilities/types";
import { FC, useCallback, useMemo, useState } from "react";
import { ReadProductVariation } from "./ReadProductVariation";
import { formatDate } from "@utilities/functions";

interface Props {
    isOpen: boolean;
    product: Product | null;
    invertories: InvetoryHistoryType | null;
    onClose: () => void;
    onAddNewInventory: (newInventory: Partial<CreateNewInventoryType>) => void;
}

export const InventoryModal:FC<Props> = ({
    isOpen,
    product,
    invertories,
    onClose,
    onAddNewInventory,
})=> {

    const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
    const [newProductVariation, setNewProductVariation] = useState<Variations | null>(null);
    const [note, setNote] = useState<string>("");

    const productInventories = useMemo(()=> {
        if(!invertories) return [];

        return invertories.details.filter((inventory) => inventory._id === product?._id);
    }, [invertories, product]);

    const handleClose = useCallback(() => {
        setSelectedOptionIndex(0);
        setNewProductVariation(null);
        setNote("");
        onClose();
    }, [onClose]);

    const handleAddNewInventory = useCallback(() => {
        if(!newProductVariation) return;

        onAddNewInventory({
            actualQuantity: newProductVariation,
            note,
        });
        handleClose();
    }, [newProductVariation, note, handleClose, onAddNewInventory]);

    const renderCurrentStock = useCallback(()=> (
        <div className="mt-6">
            <div className="relative">
                <h2 className="text-xl font-bold border-b pb-2 mb-4 text-primary">
                    Stock actuel
                </h2>

                <Button
                    className="absolute top-0 right-0 btn-sm"
                    variant='accent'
                    onClick={()=> setNewProductVariation(product?.variations!)}
                >
                    <Icon iconName='Plus' size={20} /> Nouvel inventaire
                </Button>
            </div>

            <div className="space-y-4">
                <ReadProductVariation 
                    variation={product?.variations}
                />

                {newProductVariation && (
                    <div className="mt-6">
                        <h3 className="font-bold pb-2 text-primary">
                            Nouvel inventaire
                        </h3>

                        <div className="space-y-4">
                        {'sizes' in newProductVariation ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {newProductVariation.sizes.map((size, i) => (
                                <InputWrapper key={i} label={`Taille : ${size.size}`}>
                                <Input
                                    type="number"
                                    min={0}
                                    value={size.quantity}
                                    onChange={(e) => {
                                    const quantity = parseInt(e.target.value);
                                    setNewProductVariation((prev: any) => ({
                                        ...prev,
                                        sizes: prev.sizes.map((s: any, index: number) =>
                                        index === i
                                            ? { ...s, quantity: isNaN(quantity) ? 0 : quantity }
                                            : s
                                        ),
                                    }));
                                    }}
                                />
                                </InputWrapper>
                            ))}
                            </div>
                        ) : 'colors' in newProductVariation ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {newProductVariation.colors.map((color, i) => (
                                <div key={i} className="flex items-center gap-3">
                                <span
                                    className="inline-block w-6 h-6 rounded-full border"
                                    style={{ backgroundColor: color.color }}
                                />
                                <InputWrapper label={`Quantité`}>
                                    <Input
                                        type="number"
                                        min={0}
                                        value={color.quantity}
                                        onChange={(e) => {
                                            const quantity = parseInt(e.target.value);
                                            setNewProductVariation((prev: any) => ({
                                            ...prev,
                                            colors: prev.colors.map((c: any, index: number) =>
                                                index === i
                                                ? { ...c, quantity: isNaN(quantity) ? 0 : quantity }
                                                : c
                                            ),
                                            }));
                                        }}
                                    />
                                </InputWrapper>
                                </div>
                            ))}
                            </div>
                        ) : 'colorSizeData' in newProductVariation ? (
                            <div className="space-y-3">
                                {newProductVariation.colorSizeData.map((colorSize, i) => (
                                    <div key={i} className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span
                                        className="inline-block w-4 h-4 rounded-full border"
                                        style={{ backgroundColor: colorSize.color }}
                                        />
                                        <span className="text-sm font-semibold">Couleur</span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {colorSize.sizes.map((size, j) => (
                                        <InputWrapper key={j} label={`Taille: ${size.size}`}>
                                            <Input
                                                type="number"
                                                min={0}
                                                placeholder="ex: 10"
                                                value={size.quantity}
                                                onChange={(e) => {
                                                    const quantity = parseInt(e.target.value);
                                                    setNewProductVariation((prev: any) => ({
                                                    ...prev,
                                                    colorSizeData: prev.colorSizeData.map(
                                                        (colorSizeEntry: any) => {
                                                        if (colorSizeEntry.color === colorSize.color) {
                                                            return {
                                                            ...colorSizeEntry,
                                                            sizes: colorSizeEntry.sizes.map((s: any) => {
                                                                if (s.size === size.size) {
                                                                return {
                                                                    ...s,
                                                                    quantity: isNaN(quantity) ? 0 : quantity,
                                                                };
                                                                }
                                                                return s;
                                                            }),
                                                            };
                                                        }
                                                        return colorSizeEntry;
                                                        }
                                                    ),
                                                    }));
                                                }}
                                            />
                                        </InputWrapper>
                                        ))}
                                    </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <InputWrapper label="Quantité">
                            <Input
                                type="number"
                                min={0}
                                placeholder="ex: 10"
                                value={newProductVariation.quantity}
                                onChange={(e) => {
                                const quantity = parseInt(e.target.value);
                                setNewProductVariation((prev: any) => ({
                                    ...prev,
                                    quantity: isNaN(quantity) ? 0 : quantity,
                                }));
                                }}
                            />
                            </InputWrapper>
                        )}

                        <InputWrapper label="Note">
                            <Input
                                type="textarea"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Ajouter une note..."
                            />
                        </InputWrapper>

                        <div className="flex items-center justify-between pt-2">
                            <Button
                                variant="error"
                                onClick={() => {
                                    setNewProductVariation(null);
                                    setNote('');
                                }}
                            >
                                Annuler
                            </Button>
                            <Button 
                                variant="success"
                                onClick={handleAddNewInventory}
                            >
                                Enregistrer
                            </Button>
                        </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    ), [ product, newProductVariation, note, handleAddNewInventory ]);

    const renderInventoryHistory = useCallback(()=> (
        <div className="mt-6">
            <h2 className="text-xl font-bold border-b pb-2 mb-4 text-primary">
                Historique des inventaires
            </h2>

            <div className="space-y-4">
                {productInventories.length === 0 ? (
                    <p>Aucun historique d’inventaire disponible.</p>
                ) : (
                    productInventories.map((item) => (
                        <div
                            key={item._id}
                            className="p-4 border rounded-lg bg-white shadow-sm space-y-1"
                        >
                            <p className="text-sm text-gray-600"><strong>Produit :</strong> {item.productName}</p>
                            <p className="text-sm">
                                <strong>Statut :</strong>{' '}
                                <span className={item.status === 'Conforme' ? 'text-green-600' : 'text-red-600'}>
                                {item.status}
                                </span>
                            </p>
                            <p className="text-sm">
                                <strong>Note :</strong> {item.note || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                                {formatDate(item.createdAt)}
                            </p>
                        </div>
                    ))
                )}
            </div>
        </div>
    ), [productInventories]);

    const renderSelectedOption = useCallback(() => (
        <div className="mt-4 space-y-6">
            <div className={selectedOptionIndex === 0 ? "block" : "hidden"}>
                {renderCurrentStock()}
            </div>
            <div className={selectedOptionIndex === 1 ? "block" : "hidden"}>
                {renderInventoryHistory()}
            </div>
        </div>
    ), [selectedOptionIndex, renderCurrentStock, renderInventoryHistory]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Invetaire de ${product?.name}`}
        >
            <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
                <Button 
                    className={`${selectedOptionIndex === 0 ? "btn-success" : "btn-neutral"}`}
                    onClick={()=> setSelectedOptionIndex(0)}
                >
                    <Icon iconName='Cuboid' size={20} /> Stock actuel
                </Button>

                <Button 
                    className={`${selectedOptionIndex === 1 ? "btn-success" : "btn-neutral"}`}
                    onClick={()=> setSelectedOptionIndex(1)}
                >
                    <Icon iconName='History' size={20} /> Historique des inventaires
                </Button>
            </div>

            {renderSelectedOption()}
        </Modal>
    )
}
